import { Button, Card, Grid, Group, Text, TextInput } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { validateHandlebarsValue } from '../../helpers/validateHandlebars';
import UnlayerEditor from '../Editors/UnlayerEditor';
import MergeTagsCollapsible from '../MergeTagsCollapsible';
import mergeTagsForEmailEditor from '../mergeTagsForEmailEditor';
import styles from './AppInboxEditor.module.css';

const UNLAYER_PROJECT_ID = '65057';

const RESPONSIVE_CSS =
  '<style>.u-row{display:flex;flex-wrap:nowrap;margin-left:0;margin-right:0}.u-row .u-col{position:relative;width:100%;padding-right:0;padding-left:0}.u-row .u-col.u-col-16p67{flex:0 0 16.67%;max-width:16.67%}.u-row .u-col.u-col-25{flex:0 0 25%;max-width:25%}.u-row .u-col.u-col-33p33{flex:0 0 33.33%;max-width:33.33%}.u-row .u-col.u-col-50{flex:0 0 50%;max-width:50%}.u-row .u-col.u-col-100{flex:0 0 100%;max-width:100%}@media (max-width:767px){.u-row:not(.no-stack){flex-wrap:wrap}.u-row:not(.no-stack) .u-col{flex:0 0 100%!important;max-width:100%!important}}.layout-mobile .u-row:not(.no-stack){flex-wrap:wrap}.layout-mobile .u-row:not(.no-stack) .u-col{flex:0 0 100%!important;max-width:100%!important}@media (max-width:480px){.hide-mobile{display:none!important}.container{max-width:100%!important}}@media (min-width:481px) and (max-width:768px){.hide-tablet{display:none!important}}@media (min-width:481px){.hide-desktop{display:none!important}}</style>';

const AppInboxEditor = forwardRef(({ inboxData, onChange, mergeTags, extraActions }, ref) => {
  const unlayerEditor = useRef(null);
  const [dynamicFieldsOpen, setDynamicFieldsOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    exportDesign(cb) {
      if (!unlayerEditor.current) {
        cb(inboxData);
        return;
      }
      try {
        unlayerEditor.current.exportHtml(({ design, chunks }) => {
          if (!chunks?.body) {
            cb(inboxData);
            return;
          }
          const html = chunks.body
            .replace(/min-height:\s*100vh;?\s*/g, '')
            .replace(
              /<div\b[^>]*\bid="u_body"[^>]*>/,
              `${RESPONSIVE_CSS}<div id="u_body" class="u_body" style="width: 100%;">`,
            );
          cb({ ...inboxData, design, html });
        });
      } catch {
        cb(inboxData);
      }
    },
  }));

  const handleField = (key, value) => {
    onChange({ ...inboxData, [key]: value });
  };

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="md">
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }}>
            <Text fw={500}>Title <Text span c="red">*</Text></Text>
            <Text size="xs" c="dimmed">
              Shown as the message header. Supports merge tags like {'{{firstName}}'}.
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <Group gap="xs" align="flex-start" wrap="nowrap">
              <TextInput
                className={styles.titleInput}
                value={inboxData.title || ''}
                placeholder="e.g. Hi {{firstName}}, check this out!"
                error={
                  inboxData.title === ''
                    ? 'Title is required'
                    : validateHandlebarsValue(inboxData.title)
                }
                onChange={(e) => handleField('title', e.target.value)}
              />
              <Button size="sm" variant="default" onClick={() => setDynamicFieldsOpen(!dynamicFieldsOpen)}>
                <IconInfoCircle size={14} /> Merge Tags
              </Button>
              {extraActions}
            </Group>
            <MergeTagsCollapsible open={dynamicFieldsOpen} mergeTags={mergeTags} />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={12}>
            <UnlayerEditor
              editorHeight="600px"
              design={inboxData.design}
              editorRef={unlayerEditor}
              displayMode="web"
              projectId={UNLAYER_PROJECT_ID}
              mergeTags={mergeTagsForEmailEditor(mergeTags)}
            />
          </Grid.Col>
        </Grid>
      </Card.Section>
    </Card>
  );
});

AppInboxEditor.displayName = 'AppInboxEditor';

export default AppInboxEditor;
